/**
 * player.ts — playback for all game sounds via Howler.js.
 *
 * Howler is the de-facto standard web-game audio library. Its `autoUnlock`
 * (on by default) plays a silent buffer on the first user gesture to unlock
 * the audio context — the exact mobile-Safari problem that earlier hand-rolled
 * AudioContext fixes never solved reliably. It also handles overlapping plays
 * (per-sound pooling) and cross-browser quirks for us.
 *
 * We keep our procedural synthesis: each sound is rendered once via an
 * OfflineAudioContext (no autoplay gating) into a WAV data URI, which Howler
 * loads with no network request. That avoids shipping audio asset files and
 * keeps every sound tweakable in code (see synth.ts).
 *
 * iOS silent-switch fix: on iPhone, Web Audio (and HTML5 audio) is routed to
 * the *ringer* channel by default, so the built-in speaker is muted whenever
 * the physical silent switch is on — while Bluetooth/headphone output ignores
 * that switch (the reported "works on a Bluetooth speaker but not the iPhone
 * speaker" symptom). Setting `navigator.audioSession.type = 'playback'`
 * (WebKit, iOS 16.4+) re-routes our audio to the *media* channel, which plays
 * through the built-in speaker regardless of the silent switch. Feature-detected
 * and a no-op elsewhere (Android, desktop, older iOS).
 */

import { Howl, Howler } from 'howler';
import {
  SOUND_DEFS,
  buildBattleLoop,
  BATTLE_LOOP_DURATION_SEC,
  type SoundName,
} from './synth';
import { audioBufferToWavDataUri, floatToWavDataUri } from './wav';
import { THEME_MUSIC, type ThemeName } from './soundManifest';
import { logClientError } from '../../api/clientLogApi';

/** Fixed render rate for baked clips. */
const RENDER_SAMPLE_RATE = 44100;
/** Single channel — every game sound is mono. */
const RENDER_CHANNELS = 1;
/** Battle music playback level (0..1) — quiet so it sits behind SFX. */
const MUSIC_VOLUME = 0.16;

/**
 * Master gain trim. Individual SFX are balanced to peak well under 1.0, but
 * several can fire at once (e.g. levelUp + toastSuccess + wheelResultXP) and
 * sum past full scale. A global headroom trim keeps stacked plays from clipping
 * without touching per-sound amplitudes.
 */
const MASTER_VOLUME = 0.8;
if (typeof window !== 'undefined') Howler.volume(MASTER_VOLUME);

/** Set to true to silence all audio (useful for debugging audio issues). */
const AUDIO_DISABLED = false;

/**
 * User-facing "mute all game audio" preference. Distinct from AUDIO_DISABLED
 * (a dev kill-switch): this is a player choice, persisted across sessions, so
 * people who prefer their own background music can silence the game.
 *
 * When muted we do TWO things beyond zeroing Howler's volume:
 *   1. Skip enableMediaAudioSession() — on iOS that call claims the *media*
 *      channel (audioSession.type='playback'), which interrupts whatever the
 *      player is already listening to. Not touching it leaves their music
 *      playing, which is the whole point of the toggle.
 *   2. Return before .play() so no sound is even scheduled.
 * Howler.mute() is still set as a belt-and-braces guard for any play path.
 */
const AUDIO_MUTED_KEY = 'suikamon:audioMuted';

function readStoredMuted(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(AUDIO_MUTED_KEY) === '1';
  } catch {
    return false;
  }
}

let muted = readStoredMuted();
const muteListeners = new Set<() => void>();

if (typeof window !== 'undefined' && muted) Howler.mute(true);

/** Current mute preference (true = all game audio silenced by the player). */
export function isAudioMuted(): boolean {
  return muted;
}

/** Set (and persist) the mute preference, notifying subscribers. */
export function setAudioMuted(next: boolean): void {
  if (next === muted) return;
  muted = next;
  try {
    window.localStorage.setItem(AUDIO_MUTED_KEY, next ? '1' : '0');
  } catch {
    // Private-mode / storage-disabled: keep the in-memory preference anyway.
  }
  Howler.mute(next);
  if (next) music?.stop();
  muteListeners.forEach((cb) => cb());
}

/** Subscribe to mute changes (for useSyncExternalStore). Returns an unsubscribe. */
export function subscribeAudioMuted(cb: () => void): () => void {
  muteListeners.add(cb);
  return () => muteListeners.delete(cb);
}

/**
 * Minimal typing for the experimental WebKit Audio Session API. Not in lib.dom
 * yet; guarded by a runtime feature check before use.
 * https://developer.mozilla.org/en-US/docs/Web/API/AudioSession
 */
interface AudioSessionCapableNavigator extends Navigator {
  audioSession?: { type: string };
}

/**
 * Route game audio to the iOS *media* channel so the built-in speaker plays it
 * even with the silent switch on. Harmless where the API is absent. Wrapped in
 * try/catch because assigning an unsupported value can throw in some WebKit
 * builds. Called at load and again on each play, since iOS can reset the session
 * to its default (ambient/ringer) channel after an interruption.
 */
function enableMediaAudioSession(): void {
  if (typeof navigator === 'undefined') return;
  const nav = navigator as AudioSessionCapableNavigator;
  if (!nav.audioSession) return;
  try {
    nav.audioSession.type = 'playback';
  } catch {
    // Unsupported value on this WebKit build — leave the default session type.
  }
}

// Only claim the iOS media channel when the player hasn't muted us — otherwise
// we'd interrupt their own background music the moment the app loads.
if (!muted) enableMediaAudioSession();

const sounds = new Map<SoundName, Howl>();
let music: Howl | null = null;

type OfflineAudioContextCtor = typeof OfflineAudioContext;

function getOfflineCtor(): OfflineAudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  return (
    window.OfflineAudioContext ??
    (window as unknown as { webkitOfflineAudioContext?: OfflineAudioContextCtor })
      .webkitOfflineAudioContext ??
    null
  );
}

async function renderDataUri(def: {
  duration: number;
  render: (ac: BaseAudioContext) => void;
}): Promise<string | null> {
  const Ctor = getOfflineCtor();
  if (!Ctor) return null;
  const length = Math.max(1, Math.ceil(RENDER_SAMPLE_RATE * def.duration));
  const ctx = new Ctor(RENDER_CHANNELS, length, RENDER_SAMPLE_RATE);
  def.render(ctx);
  const buffer = await ctx.startRendering();
  return audioBufferToWavDataUri(buffer);
}

let bakePromise: Promise<void> | null = null;

/**
 * Renders every sound + the battle-music loop into Howl instances. Idempotent;
 * the work runs once and later callers await the same promise. Howler handles
 * unlocking/pooling, so there is nothing else to wire up.
 */
function bakeAll(): Promise<void> {
  if (bakePromise) return bakePromise;
  bakePromise = (async () => {
    if (typeof window === 'undefined') return;
    try {
      for (const name of Object.keys(SOUND_DEFS) as SoundName[]) {
        const src = await renderDataUri(SOUND_DEFS[name]);
        if (src) sounds.set(name, new Howl({ src: [src], format: ['wav'] }));
      }

      const loopSrc = floatToWavDataUri(buildBattleLoop(RENDER_SAMPLE_RATE), RENDER_SAMPLE_RATE);
      music = new Howl({ src: [loopSrc], format: ['wav'], loop: true, volume: MUSIC_VOLUME });
    } catch (err) {
      logClientError(`Sound baking failed: ${String(err)}`, 'audioPlayer');
    }
  })();
  return bakePromise;
}

// Bake clips as soon as this module loads so they are ready before first use.
if (!AUDIO_DISABLED) void bakeAll();

/** Plays a one-shot sound effect by name. No-op until baking completes. */
export function playSound(name: SoundName) {
  if (AUDIO_DISABLED || muted) return;
  enableMediaAudioSession();
  sounds.get(name)?.play();
}

/** Starts the looping battle music (no-op if already playing). */
export function startBattleMusic() {
  if (AUDIO_DISABLED || muted) return;
  pauseCurrentTheme(); // a lobby theme never plays under a fight — and stops at once, not after the hand-off
  enableMediaAudioSession();
  if (!music) {
    void bakeAll().then(() => {
      if (music && !music.playing()) music.play();
    });
    return;
  }
  if (!music.playing()) music.play();
}

/** Stops the looping battle music. */
export function stopBattleMusic() {
  if (AUDIO_DISABLED) return;
  music?.stop();
}

/**
 * Campaign / route themes keep their PLACE. Navigating used to restart the song on every page:
 * each page's effect cleanup called `stop()` (which rewinds) and the next page started the same
 * theme again from 0. Now:
 *   - leaving a theme PAUSES it (Howler keeps the position), so coming back resumes it;
 *   - the pause is deferred by THEME_HANDOFF_MS, and a page asking for the same theme inside that
 *     window cancels it — so /play → /adopt (both `home`) never even blips, including when the
 *     next page is a lazy chunk that mounts a moment later;
 *   - the position survives a full reload / PWA relaunch in the same tab via sessionStorage.
 */
const THEME_HANDOFF_MS = 1000;
const THEME_POSITION_KEY = 'suikamon:themePosition';

const themes = new Map<ThemeName, Howl>();
const themeSoundIds = new Map<ThemeName, number>();
let currentTheme: ThemeName | null = null;
let pendingThemeStop: ReturnType<typeof setTimeout> | null = null;

function readSavedThemePosition(): { theme: ThemeName; seek: number } | null {
  try {
    const raw = window.sessionStorage.getItem(THEME_POSITION_KEY);
    return raw ? (JSON.parse(raw) as { theme: ThemeName; seek: number }) : null;
  } catch {
    return null;
  }
}
let savedThemePosition = typeof window === 'undefined' ? null : readSavedThemePosition();

function saveThemePosition(): void {
  if (!currentTheme) return;
  const howl = themes.get(currentTheme);
  const id = themeSoundIds.get(currentTheme);
  if (!howl || id === undefined) return;
  const seek = howl.seek(id);
  if (typeof seek !== 'number') return;
  try {
    window.sessionStorage.setItem(THEME_POSITION_KEY, JSON.stringify({ theme: currentTheme, seek }));
  } catch {
    // Storage disabled: in-tab navigation still keeps the position, only a reload loses it.
  }
}

if (typeof window !== 'undefined') window.addEventListener('pagehide', saveThemePosition);

function cancelPendingThemeStop(): void {
  if (pendingThemeStop === null) return;
  clearTimeout(pendingThemeStop);
  pendingThemeStop = null;
}

/** Pauses the current theme right now (keeping its position) and forgets it as current. */
function pauseCurrentTheme(): void {
  cancelPendingThemeStop();
  if (!currentTheme) return;
  themes.get(currentTheme)?.pause(themeSoundIds.get(currentTheme));
  currentTheme = null;
}

/** Starts (or resumes) a campaign theme (shipped files, streamed). One theme at a time; re-asking is a no-op. */
export function startTheme(name: ThemeName) {
  if (AUDIO_DISABLED || muted) return;
  cancelPendingThemeStop();
  if (currentTheme && currentTheme !== name) pauseCurrentTheme();
  enableMediaAudioSession();
  let howl = themes.get(name);
  if (!howl) {
    howl = new Howl({ src: [THEME_MUSIC[name]], loop: true, volume: MUSIC_VOLUME });
    themes.set(name, howl);
  }
  currentTheme = name;
  const id = themeSoundIds.get(name);
  if (id !== undefined && howl.playing(id)) return;
  // A known id resumes that (paused) sound where it was; the first play (or an id Howler no
  // longer knows — it answers null) gets a fresh one.
  const playingId = (id !== undefined && howl.play(id)) || howl.play();
  themeSoundIds.set(name, playingId);
  if (id === undefined && savedThemePosition?.theme === name) {
    howl.seek(savedThemePosition.seek, playingId);
  }
  savedThemePosition = null;
}

/**
 * Leaves whichever campaign theme is playing — only if it is still `name` (or any, if omitted).
 * Paused after a short hand-off, so the next page can keep it going without a restart.
 */
export function stopTheme(name?: ThemeName) {
  if (!currentTheme || (name && name !== currentTheme)) return;
  cancelPendingThemeStop();
  pendingThemeStop = setTimeout(pauseCurrentTheme, THEME_HANDOFF_MS);
}

export { BATTLE_LOOP_DURATION_SEC };
